import {
  EXTENSION_CATALOGUE,
  activeRules,
  catalogueEntry,
  type CatalogueEntry,
  type ExtensionId,
  type IntegrationInstance,
  type IntegrationRun,
  type IntegrationRunDetail,
  type WizardStep,
  type InstanceTargets,
  type InstanceTarget,
  type ParkedWorklog,
  type ParkedSummary,
  type UnroutedTask,
} from '@forecastify/shared';
import {
  Badge,
  Button,
  DropdownMenu,
  Icon,
  ListPager,
  ListPagerTotal,
  SKELETON_WIDTHS,
  Select,
  Skeleton,
  Switch,
  Tabs,
  cn,
  skeletonWidth,
} from '@forecastify/ui';
import type { Pages } from '../use-pages';
import { ParkedTab, type ParkedQuery } from './parked-tab';
import { UnroutedTab } from './unrouted-tab';
import { LinksTab } from './links-tab';
import { useT, type Translate } from '../../i18n';
import {
  ProviderIcon,
  extensionName,
  RuleChips,
  StateBadge,
  actionLabel,
  entityLabel,
  mapSummary,
  noteText,
  runBadge,
  runErrorText,
  ruleLabel,
  stateBadge,
} from './integration-bits';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C33 · The three integration views. `2fazis.markup.html` 2462–2795.        │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The mockup keeps all three behind one `igView` variable; R-8 gives each an address —
 * `/settings/integrations`, `/settings/integrations/:extensionId` and
 * `/settings/integrations/:extensionId/:instanceId` — so a failing instance is a link somebody can
 * paste into a chat, which `igView: 'instance'` cannot be.
 *
 * ⚠️⚠️ **THE INSTALL IS A SWITCH ON THIS CARD NOW - item 68, decision D7.** It used to live in a
 * full-screen marketplace with two addresses of its own, a settings-nav entry and a card layout,
 * for a decision with two values on two extensions. `workspace_extensions` already WAS the
 * per-workspace toggle: a row means on, `extension.service.ts` writes it, and
 * `integration.service.ts` already refuses to create an instance without one. So the endpoint, the
 * row and the live topic are unchanged and only the screen moved.
 *
 * ⚠️ **A card is DEAD until its switch is on**, and the server agrees - an instance for an
 * extension this workspace has not enabled is refused. A card that opened a wizard which then 404s
 * would be worse than one that says so.
 *
 * ⚠️ **Turning it off REFUSES while instances exist** (D7's careful part), and the refusal names
 * them. The old marketplace deleted the row and left the instances behind - still holding
 * credentials, still scheduled, and belonging to an extension the workspace no longer had.
 */

export function IntegrationsCatalogue({
  loading = false,
  instances,
  installed,
  canManage = true,
  busy = false,
  onOpenProvider,
  onInstall,
  onUninstall,
}: {
  /** Until the first answer: no figures, and no card claims to be switched off. */
  readonly loading?: boolean;
  readonly instances: readonly IntegrationInstance[];
  readonly installed: ReadonlySet<string>;
  /** ⚠️ `settings.manage` - the same action the server checks, asked once. */
  readonly canManage?: boolean;
  readonly busy?: boolean;
  readonly onOpenProvider: (id: ExtensionId) => void;
  readonly onInstall: (id: ExtensionId) => void;
  readonly onUninstall: (id: ExtensionId) => void;
}) {
  const t = useT();

  return (
    <div
      data-screen="settings-integrations"
      className="mx-auto flex w-full max-w-[880px] flex-col gap-3 p-5"
    >
      {/* ⚠️ C67 · `text-[17px] font-semibold`, which is what every other settings screen's title
          is — this one was 14px and read as a card heading rather than the page's name. The lede
          goes with the sweep that took every other settings lede. */}
      <h1 className="truncate text-[17px] font-semibold">{t('integrations.heading')}</h1>

      <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
        {EXTENSION_CATALOGUE.map((entry) => {
          const mine = instances.filter((one) => one.extensionId === entry.id);
          const bad = mine.filter((one) => one.state === 'error').length;
          const usable = installed.has(entry.id);

          return (
            /**
             * ⚠️⚠️ **A `<div>` WITH A BUTTON INSIDE IT, WHERE THE WHOLE CARD USED TO BE ONE
             * BUTTON.** The switch had to go somewhere, and a `<button>` inside a `<button>` is
             * invalid HTML that browsers repair by hoisting the inner one out - so the layout
             * breaks in a way no typecheck sees and the outer click fires on both. The card's own
             * click moved onto the name row, which is the part that means *open this*.
             */
            <div
              key={entry.id}
              className={[
                'bg-card text-card-foreground flex flex-col gap-2.5 rounded-xl border p-3.5 text-left shadow-sm transition-colors',
                usable ? 'hover:border-primary/50' : loading ? '' : 'opacity-60',
              ].join(' ')}
            >
              <div className="flex items-start gap-2.5">
                <ProviderIcon
                  entry={entry}
                  size={18}
                  className="grid size-10 shrink-0 place-items-center rounded-xl"
                />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[13.5px] font-medium">
                    {extensionName(t, entry.id)}
                  </div>
                  <div className="text-muted-foreground truncate text-[11.5px]">{entry.vendor}</div>
                </div>

                {/**
                 * ⚠️⚠️ **THE SWITCH IS THE INSTALL - item 68.** `Switch` REQUIRES a `label`,
                 * which it carries in `aria-label` — so it needs no visible label beside it; the card's heading is
                 * already the subject. Disabled without `settings.manage`, because the server
                 * refuses it and a control that answers 403 is worse than one that is plainly off
                 * limits.
                 */}
                {loading ? (
                  <Skeleton width="32px" className="h-[18px] shrink-0 rounded-full" />
                ) : (
                  <Switch
                    checked={usable}
                    disabled={!canManage || busy}
                    label={t('integrations.enable')}
                    onCheckedChange={(next) => {
                      if (next) onInstall(entry.id);
                      else onUninstall(entry.id);
                    }}
                  />
                )}
              </div>
              <p className="text-muted-foreground text-[12px] leading-relaxed">
                {t(`ext.${entry.id}.tagline`)}
              </p>
              <div className="mt-auto flex items-center gap-2 pt-0.5">
                {loading ? (
                  <span className="flex h-5 w-[110px] items-center">
                    <Skeleton width="100%" />
                  </span>
                ) : (
                  <Badge
                    variant={
                      !usable
                        ? 'outline'
                        : bad > 0
                          ? 'destructive'
                          : mine.length > 0
                            ? 'secondary'
                            : 'outline'
                    }
                  >
                    {!usable
                      ? t('integrations.notInstalled')
                      : mine.length === 0
                        ? t('integrations.noInstances')
                        : bad > 0
                          ? t('integrations.instanceCountBad', { count: mine.length, bad })
                          : t('integrations.instanceCount', { count: mine.length })}
                  </Badge>
                )}
                <span className="flex-1" />
                {usable && !loading ? (
                  <button
                    type="button"
                    onClick={() => {
                      onOpenProvider(entry.id);
                    }}
                    className="text-primary flex items-center gap-1 text-[12px] font-medium"
                  >
                    {t(mine.length > 0 ? 'integrations.manage' : 'integrations.connect')}
                    <Icon name="arrow-right" size={13} />
                  </button>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function ProviderInstances({
  loading = false,
  entry,
  instances,
  onBack,
  onOpen,
  onNew,
  onSync,
  onToggle,
  onDelete,
}: {
  /** Until the first answer: skeleton rows and no empty state. */
  readonly loading?: boolean;
  readonly entry: CatalogueEntry;
  readonly instances: readonly IntegrationInstance[];
  readonly onBack: () => void;
  readonly onOpen: (id: string) => void;
  readonly onNew: () => void;
  readonly onSync: (instance: IntegrationInstance) => void;
  readonly onToggle: (instance: IntegrationInstance, active: boolean) => void;
  readonly onDelete: (instance: IntegrationInstance) => void;
}) {
  const t = useT();

  return (
    <div
      data-screen="integration-provider"
      className="mx-auto flex w-full max-w-[880px] flex-col gap-3 p-5"
    >
      <Crumbs t={t} onBack={onBack} current={extensionName(t, entry.id)} />

      <div className="bg-card text-card-foreground flex flex-wrap items-center gap-3 rounded-xl border p-4 shadow-sm">
        <ProviderIcon entry={entry} size={20} />
        <div className="min-w-0 flex-1">
          <div className="text-[14px] font-medium">{extensionName(t, entry.id)}</div>
          <p className="text-muted-foreground mt-0.5 text-[12px] leading-relaxed">
            {t(`ext.${entry.id}.tagline`)}
          </p>
        </div>
        <Button size="sm" className="gap-1.5" onClick={onNew}>
          <Icon name="plus" size={14} />
          {t('integrations.new')}
        </Button>
      </div>

      <div className="bg-card text-card-foreground overflow-hidden rounded-xl border shadow-sm">
        <div className="text-muted-foreground bg-muted/40 flex items-center gap-3 border-b px-3 py-1.5 text-[11px] font-medium tracking-wide uppercase">
          <span className="min-w-0 flex-1">{t('integrations.col.instance')}</span>
          <span className="w-[92px] shrink-0">{t('integrations.col.state')}</span>
          <span className="hidden w-[120px] shrink-0 lg:block">{t('integrations.col.last')}</span>
          <span className="hidden w-[104px] shrink-0 xl:block">
            {t('integrations.col.schedule')}
          </span>
          <span className="w-7 shrink-0" />
        </div>

        {instances.map((instance) => (
          <div
            key={instance.id}
            className="hover:bg-accent/50 flex items-center gap-3 border-b px-3 py-2.5 transition-colors last:border-0"
          >
            <span
              className="size-2 shrink-0 rounded-full"
              style={{ background: stateBadge(instance.state).dot }}
              aria-hidden
            />
            <button
              type="button"
              onClick={() => {
                onOpen(instance.id);
              }}
              className="min-w-0 flex-1 text-left"
            >
              <div className="hover:text-primary truncate text-[13px] font-medium transition-colors">
                {instance.name}
              </div>
              <div className="text-muted-foreground truncate text-[11px]">
                {instance.baseUrl.replace(/^https?:\/\//u, '')}
              </div>
            </button>
            <span className="w-[92px] shrink-0">
              <StateBadge t={t} state={instance.state} />
            </span>
            <span className="text-muted-foreground hidden w-[120px] shrink-0 text-[11.5px] tabular-nums lg:block">
              {instance.lastRunAt === null
                ? t('integrations.never')
                : instance.lastRunAt.slice(0, 16).replace('T', ' ')}
            </span>
            <span className="text-muted-foreground hidden w-[104px] shrink-0 text-[11.5px] xl:block">
              {instance.state === 'active'
                ? t('integrations.overwatchAt', { at: instance.overwatchAt })
                : t('integrations.never')}
            </span>
            <span className="shrink-0">
              <DropdownMenu
                width={220}
                align="end"
                label={t('integrations.actions')}
                items={[
                  {
                    label: t('integrations.open'),
                    icon: 'arrow-right',
                    onSelect: () => {
                      onOpen(instance.id);
                    },
                  },
                  {
                    label: t('integrations.syncRecent'),
                    icon: 'refresh-cw',
                    onSelect: () => {
                      onSync(instance);
                    },
                  },
                  {
                    label: t(
                      instance.state === 'active' ? 'integrations.pause' : 'integrations.resume',
                    ),
                    icon: instance.state === 'active' ? 'square' : 'play',
                    onSelect: () => {
                      onToggle(instance, instance.state !== 'active');
                    },
                  },
                  { type: 'separator' },
                  {
                    label: t('integrations.delete'),
                    icon: 'trash',
                    destructive: true,
                    onSelect: () => {
                      onDelete(instance);
                    },
                  },
                ]}
              >
                <Button variant="ghost" size="icon-sm" aria-label={t('integrations.actions')}>
                  <Icon name="ellipsis" size={15} />
                </Button>
              </DropdownMenu>
            </span>
          </div>
        ))}

        {loading ? (
          <SkeletonRows rowClassName="py-2.5">
            {(index) => (
              <>
                <span className="bg-muted size-2 shrink-0 animate-pulse rounded-full" />
                <span className="flex min-w-0 flex-1 flex-col gap-1.5 py-1">
                  <Skeleton width={skeletonWidth(index, 0) / 2} />
                  <Skeleton width={skeletonWidth(index, 1) / 3} className="h-2.5" />
                </span>
                <span className="w-[92px] shrink-0">
                  <Skeleton width={skeletonWidth(index, 2)} />
                </span>
              </>
            )}
          </SkeletonRows>
        ) : instances.length === 0 ? (
          <div className="flex flex-col items-center gap-2 px-6 py-10 text-center">
            <span className="bg-muted grid size-10 place-items-center rounded-xl">
              <Icon name="plug" size={18} className="text-muted-foreground" />
            </span>
            <p className="text-muted-foreground max-w-[420px] text-[12.5px] leading-relaxed">
              {t('integrations.empty')}
            </p>
            <Button variant="outline" size="sm" onClick={onNew}>
              {t('integrations.new')}
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** Six placeholder rows in a card's own row shape, and one status line for all of them. */
function SkeletonRows({
  rowClassName,
  children,
}: {
  readonly rowClassName: string;
  readonly children: (index: number) => React.ReactNode;
}) {
  const t = useT();
  return (
    <>
      {SKELETON_WIDTHS.slice(0, 6).map((_widths, index) => (
        <div
          key={`skeleton-${String(index)}`}
          aria-hidden
          className={cn('flex items-center gap-3 border-b px-3 last:border-0', rowClassName)}
        >
          {children(index)}
        </div>
      ))}
      <div role="status" aria-live="polite" className="sr-only">
        {t('state.loading')}
      </div>
    </>
  );
}

function Crumbs({
  t,
  onBack,
  onProvider,
  provider,
  current,
}: {
  readonly t: Translate;
  readonly onBack: () => void;
  readonly onProvider?: () => void;
  readonly provider?: string;
  readonly current: string;
}) {
  return (
    <nav aria-label={t('integrations.heading')}>
      <div className="text-muted-foreground flex items-center gap-1.5 text-[12px]">
        <button type="button" className="hover:text-foreground transition-colors" onClick={onBack}>
          {t('integrations.heading')}
        </button>
        {provider === undefined ? null : (
          <>
            <Icon name="chevron-right" size={13} className="opacity-50" />
            <button
              type="button"
              className="hover:text-foreground transition-colors"
              onClick={onProvider}
            >
              {provider}
            </button>
          </>
        )}
        <Icon name="chevron-right" size={13} className="opacity-50" />
        <span className="text-foreground truncate font-medium">{current}</span>
      </div>
    </nav>
  );
}

export type InstanceTab = 'overview' | 'links' | 'parked' | 'unrouted' | 'runs' | 'settings';

/**
 * `0199` · which runs the log shows. `people` is the default and leaves webhook deliveries out:
 * on a live site there are hundreds a day, and they buried every run a person started.
 */
export type RunFilter = 'people' | 'webhook' | 'all';
export const RUN_FILTERS: readonly RunFilter[] = ['people', 'webhook', 'all'];

export function InstanceDetail({
  entry,
  instance,
  tab,
  links,
  onDiscover,
  parked,
  parkedSummary,
  parkedPages,
  parkedQuery,
  onParkedQuery,
  unrouted,
  unroutedPages,
  onOpenTask,
  onContracts,
  onRunOverwatch,
  onRegisterWebhook,
  onDisableWebhook,
  onManualWebhook,
  onDiscardParked,
  onPeople,
  onOpenLineItem,
  onMatchPeople,
  runs,
  runsLoading,
  runPages,
  recordPages,
  runFilter,
  onRunFilter,
  openRun,
  busy,
  onBack,
  onProvider,
  onTab,
  onOpenRun,
  onCloseRun,
  onSync,
  onSyncWindow,
  onToggle,
  onEdit,
  onDelete,
}: {
  readonly entry: CatalogueEntry;
  readonly instance: IntegrationInstance;
  readonly tab: InstanceTab;
  /** ⚠️ Null until the read lands — *"never discovered"* is a different thing and the tab says so. */
  readonly links: InstanceTargets | null;
  readonly onDiscover: () => void;
  /** ⚠️ Null until it lands — *"nothing waiting"* and *"not read yet"* are different facts. */
  readonly parked: readonly ParkedWorklog[] | null;
  /** J-4 · the inbox's totals over every row, and the page the rows above are. */
  readonly parkedSummary: ParkedSummary | null;
  readonly parkedPages: Pages;
  /** 17 Oct 2026 · the parked table's search and filters. */
  readonly parkedQuery: ParkedQuery;
  readonly onParkedQuery: (next: ParkedQuery) => void;
  /** J-3 · the synced tasks no line item claims. ⚠️ Null until it lands, like `parked`. */
  readonly unrouted: readonly UnroutedTask[] | null;
  readonly unroutedPages: Pages;
  /** Opens one of OUR tasks by its key — where a line item is picked by hand. */
  readonly onOpenTask: (key: string) => void;
  /** Where a line item's rule is written. */
  readonly onContracts: () => void;
  readonly onRunOverwatch: () => void;
  /** C39 · registering also REPAIRS, so one handler serves both — see the route's own note. */
  readonly onRegisterWebhook: () => void;
  readonly onDisableWebhook: () => void;
  /** §9.1's fallback: a client whose security team will not grant Jira admin. */
  readonly onManualWebhook: () => void;
  readonly onDiscardParked: (ids: readonly string[]) => void;
  /** ⚠️ §5.10 — a single pairing is set on the person's own page now, not here. */
  readonly onPeople: () => void;
  /** P2-32 · a row of the Kapcsolatok tab opens the line item whose target it is. */
  readonly onOpenLineItem: (target: InstanceTarget) => void;
  readonly onMatchPeople: () => void;
  readonly runs: readonly IntegrationRun[];
  /** Until the run log's first answer: skeleton rows and no figures. */
  readonly runsLoading?: boolean;
  /** `0199` · the run log pages on the server; this is the page's state and its filtered total. */
  readonly runPages: Pages;
  /** 30 Oct 2026 · the open run's records, a page at a time. */
  readonly recordPages: Pages;
  readonly runFilter: RunFilter;
  readonly onRunFilter: (filter: RunFilter) => void;
  readonly openRun: IntegrationRunDetail | null;
  readonly busy: boolean;
  readonly onBack: () => void;
  readonly onProvider: () => void;
  readonly onTab: (tab: InstanceTab) => void;
  readonly onOpenRun: (runId: string) => void;
  readonly onCloseRun: () => void;
  /** *Sync recent* — one month back, queued. */
  readonly onSync: () => void;
  /** The "…" beside it: a window somebody chooses. */
  readonly onSyncWindow: () => void;
  readonly onToggle: (active: boolean) => void;
  readonly onEdit: (step: WizardStep | null) => void;
  readonly onDelete: () => void;
}) {
  const t = useT();

  return (
    <div
      data-screen="integration-instance"
      className="mx-auto flex w-full max-w-[880px] flex-col gap-3 p-5"
    >
      <Crumbs
        t={t}
        onBack={onBack}
        onProvider={onProvider}
        provider={extensionName(t, entry.id)}
        current={instance.name}
      />

      <div className="bg-card text-card-foreground flex flex-wrap items-center gap-3 rounded-xl border p-4 shadow-sm">
        <ProviderIcon entry={entry} size={20} />
        <div className="min-w-[210px] flex-1">
          <div className="flex items-center gap-2">
            <h2 className="truncate text-[14px] font-medium">{instance.name}</h2>
            <StateBadge t={t} state={instance.state} />
          </div>
          <div className="text-muted-foreground mt-0.5 truncate text-[11.5px]">
            {instance.baseUrl}
          </div>
        </div>
        <span className="flex items-center gap-2 text-[12px]">
          <Switch
            checked={instance.state === 'active'}
            label={t('integrations.active')}
            disabled={busy}
            onCheckedChange={onToggle}
          />
          {t('integrations.active')}
        </span>
        {/* ⚠️ `0199` · a split button: the common case in one press, the chosen window behind "…".
            Two outline buttons sharing an edge rather than a new component — the design system
            has no split button, and this is the whole of it. */}
        <span
          role="group"
          aria-label={t('integrations.sync.group')}
          className="inline-flex items-center"
        >
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5 rounded-r-none"
            disabled={busy}
            onClick={onSync}
          >
            <Icon name="refresh-cw" size={14} />
            {t('integrations.syncRecent')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="-ml-px rounded-l-none px-2"
            disabled={busy}
            aria-label={t('integrations.syncWindow.open')}
            title={t('integrations.syncWindow.open')}
            onClick={onSyncWindow}
          >
            <Icon name="ellipsis" size={14} />
          </Button>
        </span>
        <DropdownMenu
          width={220}
          align="end"
          label={t('integrations.actions')}
          items={[
            {
              label: t('integrations.reopenWizard'),
              icon: 'sliders-horizontal',
              onSelect: () => {
                onEdit(null);
              },
            },
            { type: 'separator' },
            {
              label: t('integrations.delete'),
              icon: 'trash',
              destructive: true,
              onSelect: onDelete,
            },
          ]}
        >
          <Button variant="ghost" size="icon-sm" aria-label={t('integrations.actions')}>
            <Icon name="ellipsis" size={16} />
          </Button>
        </DropdownMenu>
      </div>

      <div className="border-b">
        <Tabs
          value={tab}
          variant="underline"
          /* ⚠️ Names the TABLIST, not a tab — a bare tablist has no name for a screen reader. */
          label={t('integrations.heading')}
          onValueChange={(value) => {
            onTab(value as InstanceTab);
          }}
          items={[
            { value: 'overview', label: t('integrations.tab.overview'), icon: 'chart-column' },
            /* ⚠️ Second, not last: linking is the thing somebody comes here to DO after connecting,
               and nothing syncs until they have (§9.5 — it is the only way a link is ever made). */
            { value: 'links', label: t('integrations.tab.links'), icon: 'link' },
            /* ⚠️ **After linking, before the run log**, because that is the order somebody fixes
               things in: link the projects, then look at what could not be filed anyway. */
            { value: 'parked', label: t('integrations.tab.parked'), icon: 'timer' },
            /* J-3 · beside the parked hours: the other half of what the sync could not file. */
            { value: 'unrouted', label: t('integrations.tab.unrouted'), icon: 'list-checks' },
            { value: 'runs', label: t('integrations.tab.runs'), icon: 'refresh-cw' },
            { value: 'settings', label: t('integrations.tab.settings'), icon: 'settings' },
          ]}
        />
      </div>

      {tab === 'overview' ? (
        <Overview
          t={t}
          instance={instance}
          runs={runs}
          onEdit={onEdit}
          onMatchPeople={onMatchPeople}
          onRegisterWebhook={onRegisterWebhook}
          onDisableWebhook={onDisableWebhook}
          onManualWebhook={onManualWebhook}
        />
      ) : null}

      {tab === 'links' ? (
        <LinksTab
          t={t}
          entry={entry}
          links={links}
          busy={busy}
          onDiscover={onDiscover}
          onOpenLineItem={onOpenLineItem}
        />
      ) : null}

      {tab === 'parked' ? (
        <ParkedTab
          t={t}
          rows={parked}
          summary={parkedSummary}
          pages={parkedPages}
          query={parkedQuery}
          onQuery={onParkedQuery}
          busy={busy}
          onRun={onRunOverwatch}
          onDiscard={onDiscardParked}
          onLinks={() => {
            onTab('links');
          }}
          onPeople={onPeople}
        />
      ) : null}

      {tab === 'unrouted' ? (
        <UnroutedTab
          t={t}
          rows={unrouted}
          pages={unroutedPages}
          instance={{ baseUrl: instance.baseUrl, extensionId: instance.extensionId }}
          onOpenTask={onOpenTask}
          onContracts={onContracts}
        />
      ) : null}

      {tab === 'runs' ? (
        <Runs
          t={t}
          runs={runs}
          loading={runsLoading}
          pages={runPages}
          recordPages={recordPages}
          filter={runFilter}
          onFilter={onRunFilter}
          openRun={openRun}
          onOpenRun={onOpenRun}
          onCloseRun={onCloseRun}
          onSync={onSync}
        />
      ) : null}

      {tab === 'settings' ? (
        <SettingsTab t={t} instance={instance} onEdit={onEdit} onDelete={onDelete} />
      ) : null}
    </div>
  );
}

function Overview({
  t,
  instance,
  runs,
  onEdit,
  onMatchPeople,
  onRegisterWebhook,
  onDisableWebhook,
  onManualWebhook,
}: {
  readonly t: Translate;
  readonly instance: IntegrationInstance;
  readonly runs: readonly IntegrationRun[];
  readonly onEdit: (step: WizardStep) => void;
  /** C39 cleanup · the bulk address match, the one identity action about nobody in particular. */
  readonly onMatchPeople: () => void;
  readonly onRegisterWebhook: () => void;
  readonly onDisableWebhook: () => void;
  readonly onManualWebhook: () => void;
}) {
  /* ⚠️ The mockup's *"átjött (24 óra)"* over its last three runs. Ours sums the same window over
     what the log actually holds — an aggregate over rows rather than a seeded constant. */
  const recent = runs.slice(0, 3);
  const inCount = recent.reduce((total, run) => total + run.inTasks + run.inWorklogs, 0);
  const outCount = recent.reduce((total, run) => total + run.outTasks + run.outWorklogs, 0);
  const failing = runs.filter((run) => run.state === 'error' || run.state === 'partial').length;

  const stats = [
    {
      key: 'last',
      label: t('integrations.stat.last'),
      value:
        instance.lastRunAt === null ? t('integrations.never') : instance.lastRunAt.slice(11, 16),
      note:
        instance.lastRunAt === null
          ? t('integrations.stat.lastNever')
          : instance.lastRunAt.slice(0, 10),
    },
    {
      key: 'schedule',
      label: t('integrations.stat.schedule'),
      value:
        instance.state === 'active'
          ? t('integrations.overwatchAt', { at: instance.overwatchAt })
          : t('integrations.stat.paused'),
      note: instance.state === 'active' ? '' : t('integrations.stat.scheduleOff'),
    },
    {
      key: 'in',
      label: t('integrations.stat.in'),
      value: String(inCount),
      note: t('integrations.stat.inNote', { count: outCount }),
    },
    {
      key: 'errors',
      label: t('integrations.stat.errors'),
      value: String(failing),
      note: t(failing > 0 ? 'integrations.stat.errorsNote' : 'integrations.stat.errorsNone'),
    },
  ];

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
        {stats.map((stat) => (
          <div
            key={stat.key}
            className="bg-card text-card-foreground rounded-xl border p-3 shadow-sm"
          >
            <div className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">
              {stat.label}
            </div>
            <div className="mt-1 text-[17px] tabular-nums">{stat.value}</div>
            <div className="text-muted-foreground mt-0.5 text-[11.5px]">{stat.note}</div>
          </div>
        ))}
      </div>

      <section className="bg-card text-card-foreground rounded-xl border p-4 shadow-sm">
        <h3 className="text-[13px] font-medium">{t('integrations.connection')}</h3>
        <div className="mt-2 flex flex-col">
          {[
            { k: t('integrations.conn.url'), v: instance.baseUrl },
            {
              k: t('integrations.conn.auth'),
              v: t(
                instance.authKind === 'oauth'
                  ? 'integrations.conn.oauth'
                  : 'integrations.conn.tokenKind',
              ),
            },
            {
              k: t('integrations.conn.account'),
              v:
                instance.authKind === 'oauth'
                  ? t('integrations.conn.oauthAccount')
                  : (instance.authEmail ?? '–'),
            },
            {
              /* ⚠️ The HINT, and there is nothing else to show — `0060` and the service make sure
                 the plaintext never reaches this process, let alone this component. */
              k: t('integrations.conn.token'),
              v:
                instance.authKind === 'oauth'
                  ? t('integrations.conn.noToken')
                  : (instance.authHint ?? '–'),
            },
          ].map((row) => (
            <div
              key={row.k}
              className="flex items-center gap-3 border-b py-1.5 text-[12.5px] last:border-0"
            >
              <span className="text-muted-foreground w-[110px] shrink-0">{row.k}</span>
              <span className="min-w-0 flex-1 truncate text-[12px]">{row.v}</span>
            </div>
          ))}
        </div>
      </section>

      {/*
        ⚠️⚠️ **NOTHING AT ALL FOR A PROVIDER THAT CANNOT PUSH** (product owner, 4 Sep 2026). This
        card is Jira's: `WebhookRegistrationService` posts to Atlassian's `/rest/webhooks` and is
        Jira-specific in every line, so *Register* on a YouTrack instance fired a Jira call at a
        YouTrack address and failed — the *misconfigured* reported from the server.

        ⚠️ **It went through an intermediate form worth recording**: a card saying *By polling*, on
        the reasoning that hiding it would leave the unanswered question *how does anything arrive,
        then?*. The ruling was that the question is not raised in the first place — a section about a
        mechanism this tracker does not have is noise on the one screen somebody reads to find out
        whether their integration is healthy. **Where the answer belongs is `chores.ts`, the run log,
        and the docs; not a permanent card saying nothing is here.**
      */}
      {catalogueEntry(instance.extensionId)?.pushes === false ? null : (
        <LiveUpdates
          t={t}
          instance={instance}
          onRegister={onRegisterWebhook}
          onDisable={onDisableWebhook}
          onManual={onManualWebhook}
        />
      )}

      <section className="bg-card text-card-foreground rounded-xl border p-4 shadow-sm">
        <h3 className="text-[13px] font-medium">{t('integrations.whatSyncs')}</h3>
        <RuleChips t={t} rules={instance.rules} />
        <p className="text-muted-foreground mt-2.5 text-[11.5px]">
          {t('integrations.conflict', {
            winner: t(
              instance.conflictWinner === 'remote'
                ? 'integrations.conflict.remote'
                : 'integrations.conflict.local',
            ),
          })}
        </p>
      </section>

      <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
        {mapSummary(t, instance).map((card) => (
          <div
            key={card.key}
            className="bg-card text-card-foreground flex items-start gap-2.5 rounded-xl border p-3.5 shadow-sm"
          >
            <span className="bg-muted text-muted-foreground mt-px grid size-8 shrink-0 place-items-center rounded-lg">
              <Icon name={card.icon} size={15} />
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-[13px] font-medium">{card.label}</div>
              <div className="mt-0.5 text-[12px] tabular-nums">{card.value}</div>
              <div className="text-muted-foreground mt-0.5 text-[11.5px] leading-relaxed">
                {card.note}
              </div>
            </div>
            {/**
             * ⚠️⚠️ **THE COUNT BELONGS HERE AND THE FIX DOES NOT** (§5.10, §9.10). An unrecognised
             * remote account is the only integration failure that makes a *report* wrong, so the
             * number is ranked on the instance card. Pairing one person, though, is a question
             * about that person — and C39's cleanup moved it onto their own page.
             *
             * ⚠️ **What is left here is the BULK match**, which is the one identity action that is
             * not about anybody in particular: it compares addresses across the whole workspace.
             * It had no other home once the list panel went, and beside the count is where somebody
             * is already looking at how many are unpaired.
             */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                if (card.key === 'statuses') onEdit('statuses');
                else if (card.key === 'priorities') onEdit('priorities');
                else onMatchPeople();
              }}
            >
              {card.key === 'statuses' || card.key === 'priorities'
                ? t('common.edit')
                : t('identities.match')}
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * C39 · live updates, on the instance card.
 *
 * ⚠️⚠️ **THE MOCKUP DOES NOT DRAW THIS.** Its *"webhook"* is a different feature — outbound hooks
 * on the workspace settings screen, deferred there in its own copy. So this follows
 * `docs/design-system/` and the card idiom the rest of this Overview already uses, rather than
 * inventing a shape.
 *
 * ⚠️⚠️ **THE ONE THING WORTH A WARNING IS `moved`, AND IT IS THE ONE NOBODY WOULD EVER FIND.** Jira
 * posts to the address it was given. A deployment whose public origin changes — or a tunnel
 * restarted on a new URL — leaves a subscription pointing at nothing, and **there is no error
 * anywhere, because nothing arrives**. Only the server can see it, because only the server knows
 * both the registered address and its own.
 *
 * ⚠️ **`lastDeliveryAt` is shown as a fact and never judged.** A subscription an admin deleted and
 * a Jira where nobody did anything today produce the same timestamp; §9.2's honest lapse detector
 * is drift, which is not measured yet (`debt.md`). Saying *"gone quiet"* from silence alone would
 * cry wolf on every low-traffic project.
 */
function LiveUpdates({
  t,
  instance,
  onRegister,
  onDisable,
  onManual,
}: {
  readonly t: Translate;
  readonly instance: IntegrationInstance;
  readonly onRegister: () => void;
  readonly onDisable: () => void;
  readonly onManual: () => void;
}) {
  const hook = instance.webhook;
  const state = hook?.state ?? 'off';
  /* ⚠️ A subscription somebody pasted in by hand cannot be repaired from here — we hold no id for
     it and no credential that could change it (§9.1). Saying so beats a button that fails. */
  const repairable = hook?.byHand !== true;

  const rows =
    state === 'off'
      ? []
      : [
          { k: t('integrations.webhook.address'), v: hook?.origin ?? '–' },
          {
            k: t('integrations.webhook.registeredAt'),
            v: hook?.registeredAt?.slice(0, 16).replace('T', ' ') ?? '–',
          },
          {
            k: t('integrations.webhook.lastDelivery'),
            v:
              hook?.lastDeliveryAt === null || hook?.lastDeliveryAt === undefined
                ? t('integrations.webhook.never')
                : hook.lastDeliveryAt.slice(0, 16).replace('T', ' '),
          },
        ];

  return (
    <section className="bg-card text-card-foreground rounded-xl border p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-[13px] font-medium">{t('integrations.webhook.title')}</h3>
          <p className="text-muted-foreground mt-1 text-[11.5px] leading-relaxed">
            {t(
              state === 'off'
                ? 'integrations.webhook.offNote'
                : state === 'moved'
                  ? 'integrations.webhook.movedNote'
                  : state === 'stale'
                    ? 'integrations.webhook.staleNote'
                    : 'integrations.webhook.liveNote',
            )}
          </p>
        </div>
        <Badge
          variant={
            state === 'live'
              ? 'default'
              : state === 'moved'
                ? 'destructive'
                : state === 'stale'
                  ? 'secondary'
                  : 'outline'
          }
        >
          {t(
            state === 'live'
              ? 'integrations.webhook.live'
              : state === 'moved'
                ? 'integrations.webhook.moved'
                : state === 'stale'
                  ? 'integrations.webhook.stale'
                  : 'integrations.webhook.off',
          )}
        </Badge>
      </div>

      {rows.length > 0 ? (
        <div className="mt-2 flex flex-col">
          {rows.map((row) => (
            <div
              key={row.k}
              className="flex items-center gap-3 border-b py-1.5 text-[12.5px] last:border-0"
            >
              <span className="text-muted-foreground w-[110px] shrink-0">{row.k}</span>
              <span className="min-w-0 flex-1 truncate text-[12px]">{row.v}</span>
            </div>
          ))}
        </div>
      ) : null}

      {/* ⚠️ Shown only when it is not zero, which is almost always: a count of nothing is noise, and
          a count that is climbing is either a rotated secret or somebody probing (§9.2). */}
      {/* ⚠️⚠️ **DRIFT IS §9.2'S HONEST LAPSE DETECTOR**, and the reason there is no *"gone quiet"*
          verdict anywhere here: a timestamp cannot tell a dead subscription from a quiet Jira, but
          a reconciliation that keeps finding changes nobody announced can. Shown only when it is
          not zero, because zero is the healthy state and a permanent zero is noise. */}
      {(hook?.driftLastRun ?? 0) > 0 ? (
        <p className="text-muted-foreground mt-2 text-[11.5px]">
          {t('integrations.webhook.drift', { count: hook?.driftLastRun ?? 0 })}{' '}
          {/* ⚠️ *When did it stop* is the question support opens with, and the count alone cannot
              answer it. Absent while the streak is one run old, because *since today* says nothing
              a person did not already know from the number. */}
          {hook?.driftSince === null || hook?.driftSince === undefined
            ? null
            : t('integrations.webhook.driftSince', { at: hook.driftSince.slice(0, 10) })}
        </p>
      ) : null}

      {(hook?.rejectedCount ?? 0) > 0 ? (
        <p className="text-destructive mt-2 text-[11.5px]">
          {t('integrations.webhook.rejected', { count: hook?.rejectedCount ?? 0 })}
        </p>
      ) : null}

      <div className="mt-3 flex flex-wrap gap-2">
        {repairable ? (
          <Button
            size="sm"
            variant={state === 'moved' || state === 'stale' ? 'default' : 'outline'}
            onClick={onRegister}
          >
            {t(state === 'off' ? 'integrations.webhook.turnOn' : 'integrations.webhook.repair')}
          </Button>
        ) : (
          /* ⚠️ Advice rather than a button, because the button could not work — and a control that
             fails for a reason the person cannot see is worse than no control. */
          <p className="text-muted-foreground text-[11.5px]">
            {t('integrations.webhook.byHandNote')}
          </p>
        )}
        {state === 'off' ? (
          <Button size="sm" variant="ghost" onClick={onManual}>
            {t('integrations.webhook.byHand')}
          </Button>
        ) : (
          <Button size="sm" variant="ghost" onClick={onDisable}>
            {t('integrations.webhook.turnOff')}
          </Button>
        )}
      </div>
    </section>
  );
}

/** The run log's pager and the open run's — one control, `[<] [selector] [>]`. */
function RunPager({ t, pages }: { readonly t: Translate; readonly pages: Pages }) {
  return (
    <ListPager
      page={{
        hasPrevious: pages.hasPrevious,
        hasNext: pages.hasNext,
        onPrevious: pages.previous,
        onNext: pages.next,
        index: pages.index,
        count: pages.count,
        total: pages.total,
        onGoTo: pages.goTo,
      }}
      labels={{
        previousPage: t('list.previousPage'),
        nextPage: t('list.nextPage'),
        pageSelector: t('list.pageSelector'),
        pageOf: (page, count) => t('list.pageOf', { page, count }),
      }}
    />
  );
}

function Runs({
  t,
  runs,
  loading = false,
  pages,
  recordPages,
  filter,
  onFilter,
  openRun,
  onOpenRun,
  onCloseRun,
  onSync,
}: {
  readonly t: Translate;
  readonly runs: readonly IntegrationRun[];
  readonly loading?: boolean;
  readonly pages: Pages;
  readonly recordPages: Pages;
  readonly filter: RunFilter;
  readonly onFilter: (filter: RunFilter) => void;
  readonly openRun: IntegrationRunDetail | null;
  readonly onOpenRun: (runId: string) => void;
  readonly onCloseRun: () => void;
  readonly onSync: () => void;
}) {
  if (openRun !== null) {
    const badge = runBadge(openRun.state);
    return (
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="ghost" size="sm" className="-ml-2 gap-1.5" onClick={onCloseRun}>
            <Icon name="chevron-left" size={14} />
            {t('integrations.runs.all')}
          </Button>
          <span className="text-[13px] tabular-nums">
            {openRun.startedAt.slice(0, 16).replace('T', ' ')}
          </span>
          <span className="text-muted-foreground text-[12px]">
            {t(`integrations.run.trigger.${openRun.trigger}`)}
          </span>
          <Badge variant={badge.variant}>{t(badge.key)}</Badge>
          {windowText(openRun, t) !== null ? (
            <span className="text-muted-foreground text-[12px] tabular-nums">
              {t('integrations.run.window')}: {windowText(openRun, t)}
            </span>
          ) : null}
        </div>

        <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
          {[
            {
              title: t('integrations.run.in'),
              items: [
                { k: t('integrations.run.tasks'), v: openRun.inTasks },
                { k: t('integrations.run.worklogs'), v: openRun.inWorklogs },
              ],
            },
            {
              title: t('integrations.run.out'),
              items: [
                { k: t('integrations.run.tasks'), v: openRun.outTasks },
                { k: t('integrations.run.worklogs'), v: openRun.outWorklogs },
              ],
            },
          ].map((group) => (
            <div
              key={group.title}
              className="bg-card text-card-foreground rounded-xl border p-3 shadow-sm"
            >
              <div className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">
                {group.title}
              </div>
              <div className="mt-1.5 flex items-end gap-4">
                {group.items.map((item) => (
                  <div key={item.k} className="min-w-0">
                    <div className="text-[16px] leading-none tabular-nums">{item.v}</div>
                    <div className="text-muted-foreground mt-1 text-[11px] leading-tight">
                      {item.k}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {openRun.errors.length === 0 ? null : (
          <div className="border-destructive/40 bg-destructive/5 flex flex-col gap-1.5 rounded-xl border p-3.5">
            <div className="text-destructive text-[12.5px] font-medium">
              {t('integrations.run.errors')}
            </div>
            {openRun.errors.map((raw) => (
              <p key={raw} className="text-[12px] leading-relaxed">
                {runErrorText(t, raw)}
              </p>
            ))}
          </div>
        )}

        <div className="bg-card text-card-foreground overflow-hidden rounded-xl border shadow-sm">
          <div className="text-muted-foreground bg-muted/40 flex items-center gap-3 border-b px-3 py-1.5 text-[11px] font-medium tracking-wide uppercase">
            <span className="w-[62px] shrink-0">{t('integrations.run.col.entity')}</span>
            <span className="w-[84px] shrink-0">{t('integrations.run.col.key')}</span>
            <span className="w-[34px] shrink-0">{t('integrations.run.col.dir')}</span>
            <span className="min-w-0 flex-1">{t('integrations.run.col.action')}</span>
            <span className="hidden w-[68px] shrink-0 lg:block">
              {t('integrations.run.col.result')}
            </span>
          </div>
          {openRun.records.map((record) => (
            /**
             * ⚠️⚠️ **A `<details>`, NOT A CONTROLLED ROW - item 67.** The payload, the task and
             * the project are what somebody opens this log to find, and they are also far too much
             * to put on every one of 1,300 rows. `<details>` gives the disclosure, the keyboard
             * behaviour and the open state for nothing - a `useState` per row would be the same
             * thing with a re-render and a bug about which row is open.
             *
             * ⚠️ **The summary is the row that was always here**, unchanged, so a reader who
             * wants none of this sees exactly what they saw before.
             */
            <details key={record.id} className="group border-b last:border-0 open:bg-muted/30">
              <summary className="flex cursor-pointer list-none items-start gap-3 px-3 py-2 text-[12.5px]">
                <span className="text-muted-foreground w-[62px] shrink-0">
                  {entityLabel(t, record.entity)}
                </span>
                <span className="w-[84px] shrink-0 truncate text-[11.5px]">{record.remoteKey}</span>
                <span className="w-[34px] shrink-0">
                  <span
                    className={[
                      'rounded border px-1 py-px text-[10.5px] font-medium',
                      /* ⚠️ Tokens, not the mockup's `#2F6FEB` — see `integration-bits.tsx`. `in` is
                       the informational hue, `out` the product's own. */
                      record.direction === 'in'
                        ? 'text-[var(--status-info)] border-[var(--status-info)]/40 bg-[var(--status-info)]/10'
                        : 'text-primary border-primary/40 bg-primary/10',
                    ].join(' ')}
                  >
                    {t(
                      record.direction === 'in'
                        ? 'integrations.run.dir.in'
                        : 'integrations.run.dir.out',
                    )}
                  </span>
                </span>
                <span className="min-w-0 flex-1">
                  <span className="font-medium">{actionLabel(t, record.action)}</span>
                  <span className="text-muted-foreground"> · {noteText(t, record.note)}</span>
                </span>
                <span
                  className={[
                    'hidden w-[68px] shrink-0 text-[11.5px] lg:block',
                    record.result === 'ok'
                      ? 'text-muted-foreground'
                      : record.result === 'skip'
                        ? 'text-amber-600'
                        : 'text-destructive',
                  ].join(' ')}
                >
                  {t(
                    record.result === 'ok'
                      ? 'integrations.run.result.ok'
                      : record.result === 'skip'
                        ? 'integrations.run.result.skip'
                        : 'integrations.run.result.error',
                  )}
                </span>
              </summary>

              <div className="flex flex-col gap-2 px-3 pt-1 pb-3">
                {/**
                 * ⚠️⚠️ **THE ROW IT TOUCHED, WHICH IS THE HALF PEOPLE ACTUALLY CAME FOR.** A key
                 * and a verb told a reader what the tracker called something and nothing about
                 * what this product did with it. Both are nullable and the reason is ordinary:
                 * a skipped record touched nothing, and a task somebody has since deleted leaves
                 * a log line that outlives it (`0103` refuses a foreign key for exactly that).
                 */}
                {record.task === null && record.project === null ? null : (
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[12px]">
                    {record.task === null ? null : (
                      <span className="min-w-0">
                        <span className="text-muted-foreground">
                          {t('integrations.run.onTask')}{' '}
                        </span>
                        {record.task.title}
                      </span>
                    )}
                    {record.project === null ? null : (
                      <span className="min-w-0">
                        <span className="text-muted-foreground">
                          {t('integrations.run.inProject')}{' '}
                        </span>
                        {record.project.name}
                      </span>
                    )}
                  </div>
                )}

                {/**
                 * ⚠️⚠️ **FORMATTED JSON, AND THAT IS THE ONLY HONEST RENDERING.** The shape
                 * belongs to Jira or YouTrack, differs per site and per custom field, and changes
                 * when a vendor ships - a table of named rows would be a schema we do not own and
                 * would quietly drop whatever it had not been taught. `overwatch.service.ts` caps
                 * the size and marks a trimmed one as trimmed.
                 *
                 * ⚠️ **`Inter`, not a monospace face** (CLAUDE.md §3: one font family). What
                 * makes JSON readable here is the indentation and the wrapping, both of which the
                 * `<pre>` provides; a second family would be a control the design system does not
                 * have.
                 */}
                {record.payload == null ? (
                  <p className="text-muted-foreground text-[11.5px]">
                    {t('integrations.run.noPayload')}
                  </p>
                ) : (
                  <pre className="bg-muted max-h-[240px] overflow-auto rounded-md p-2.5 text-[11.5px] leading-relaxed break-words whitespace-pre-wrap">
                    {JSON.stringify(record.payload, null, 2)}
                  </pre>
                )}
              </div>
            </details>
          ))}
          {openRun.records.length === 0 ? (
            <p className="text-muted-foreground px-3 py-8 text-center text-[12.5px]">
              {t('integrations.run.noRecords')}
            </p>
          ) : null}
        </div>
        {/* 30 Oct 2026 · fifty records a page — a three-year backfill is tens of thousands. */}
        <div className="flex items-center justify-end">
          <RunPager t={t} pages={recordPages} />
        </div>
      </div>
    );
  }

  /**
   * ⚠️ `0199` · the toolbar stays whatever the page holds: an empty *started and scheduled* list
   * is not an empty log, and the filter is how somebody finds out.
   */
  const toolbar = (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <Select
        aria-label={t('integrations.runs.filter.label')}
        value={filter}
        onChange={(event) => {
          const next = event.target.value;
          if (RUN_FILTERS.includes(next as RunFilter)) onFilter(next as RunFilter);
        }}
        options={RUN_FILTERS.map((one) => ({
          value: one,
          label: t(`integrations.runs.filter.${one}`),
        }))}
        className="w-[220px]"
      />
      <span className="text-muted-foreground text-[12px]">
        <ListPagerTotal
          total={loading ? undefined : pages.total}
          labels={{ pageTotal: (total) => t('integrations.runs.total', { total }) }}
        />
      </span>
    </div>
  );

  if (runs.length === 0 && !loading) {
    return (
      <div className="flex flex-col gap-3">
        {toolbar}
        <div className="bg-card text-card-foreground flex flex-col items-center gap-2 rounded-xl border p-10 text-center shadow-sm">
          <span className="bg-muted grid size-10 place-items-center rounded-xl">
            <Icon name="refresh-cw" size={18} className="text-muted-foreground" />
          </span>
          <p className="text-muted-foreground max-w-[420px] text-[12.5px] leading-relaxed">
            {t('integrations.runs.empty')}
          </p>
          <Button variant="outline" size="sm" onClick={onSync}>
            {t('integrations.syncRecent')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {toolbar}
      <div className="bg-card text-card-foreground overflow-hidden rounded-xl border shadow-sm">
        <div className="text-muted-foreground bg-muted/40 flex items-center gap-3 border-b px-3 py-1.5 text-[11px] font-medium tracking-wide uppercase">
          <span className="w-[112px] shrink-0">{t('integrations.runs.col.at')}</span>
          <span className="hidden w-[76px] shrink-0 xl:block">
            {t('integrations.runs.col.trigger')}
          </span>
          <span className="min-w-0 flex-1">{t('integrations.runs.col.result')}</span>
          <span className="w-[38px] shrink-0 text-right">{t('integrations.runs.col.in')}</span>
          <span className="w-[38px] shrink-0 text-right">{t('integrations.runs.col.out')}</span>
        </div>
        {loading ? (
          <SkeletonRows rowClassName="py-2">
            {(index) => (
              <>
                <span className="flex h-5 w-[112px] shrink-0 items-center">
                  <Skeleton width={skeletonWidth(index, 0)} />
                </span>
                <span className="hidden w-[76px] shrink-0 xl:block">
                  <Skeleton width={skeletonWidth(index, 1)} />
                </span>
                <span className="min-w-0 flex-1">
                  <Skeleton width={skeletonWidth(index, 2) / 2} />
                </span>
                <span className="w-[38px] shrink-0">
                  <Skeleton width={skeletonWidth(index, 3)} className="ml-auto" />
                </span>
                <span className="w-[38px] shrink-0">
                  <Skeleton width={skeletonWidth(index, 4)} className="ml-auto" />
                </span>
              </>
            )}
          </SkeletonRows>
        ) : null}
        {runs.map((run) => {
          const badge = runBadge(run.state);
          const window = windowText(run, t);
          /**
           * J-8 · what the row is about, where the badge alone said *Succeeded* 400 times over:
           * the project a `target` pull read, the window a backfill read, and for a run still
           * going, how long it has been — the list refreshes on every sync event, so this ticks.
           */
          const about =
            run.remoteProjectKey ??
            (run.remoteProjectId !== null && run.trigger === 'target' ? run.remoteProjectId : null);
          const elapsed =
            run.state === 'running'
              ? t('integrations.run.elapsed', {
                  minutes: Math.max(
                    0,
                    Math.round((Date.now() - Date.parse(run.startedAt)) / 60_000),
                  ),
                })
              : null;
          const breakdown =
            run.inTasks + run.inWorklogs > 0
              ? t('integrations.run.inBreakdown', { tasks: run.inTasks, worklogs: run.inWorklogs })
              : null;
          return (
            <button
              key={run.id}
              type="button"
              onClick={() => {
                onOpenRun(run.id);
              }}
              className="hover:bg-accent/50 flex w-full items-center gap-3 border-b px-3 py-2 text-left text-[12.5px] transition-colors last:border-0"
            >
              <span className="w-[112px] shrink-0 text-[11.5px] tabular-nums">
                {run.startedAt.slice(0, 16).replace('T', ' ')}
              </span>
              <span className="text-muted-foreground hidden w-[76px] shrink-0 xl:block">
                {t(`integrations.run.trigger.${run.trigger}`)}
              </span>
              <span className="flex min-w-0 flex-1 items-center gap-2">
                <Badge variant={badge.variant}>{t(badge.key)}</Badge>
                {about !== null ? (
                  <span className="truncate text-[11.5px] tabular-nums">{about}</span>
                ) : null}
                {/* ⚠️ `0199` · the window a backfill read is the whole reason its row exists. */}
                {window !== null ? (
                  <span className="text-muted-foreground truncate text-[11.5px] tabular-nums">
                    {window}
                  </span>
                ) : null}
                {elapsed !== null ? (
                  <span className="text-muted-foreground truncate text-[11.5px]">{elapsed}</span>
                ) : null}
                {breakdown !== null ? (
                  <span className="text-muted-foreground hidden truncate text-[11.5px] tabular-nums lg:inline">
                    {breakdown}
                  </span>
                ) : null}
                <span
                  className={cn(
                    'truncate text-[11.5px]',
                    run.state === 'ok' ? 'text-muted-foreground' : 'text-destructive',
                  )}
                >
                  {run.errors.length === 0
                    ? ''
                    : t(
                        run.state === 'ok'
                          ? 'integrations.run.noteCount'
                          : 'integrations.run.errorCount',
                        { count: run.errors.length },
                      )}
                </span>
              </span>
              <span className="w-[38px] shrink-0 text-right tabular-nums">
                {run.inTasks + run.inWorklogs}
              </span>
              <span className="w-[38px] shrink-0 text-right tabular-nums">
                {run.outTasks + run.outWorklogs}
              </span>
            </button>
          );
        })}
      </div>
      <div className="flex items-center justify-end">
        <RunPager t={t} pages={pages} />
      </div>
    </div>
  );
}

/**
 * `0199` · *"1 Jun – 14 Aug"*, or *"14 Aug – until now"*. Null where the run read no window — a
 * webhook delivery, a retry sweep — so the column stays quiet rather than saying *until now* about
 * a delivery that had no window at all.
 */
function windowText(run: IntegrationRun, t: Translate): string | null {
  if (run.windowFrom === null) return null;
  const from = run.windowFrom.slice(0, 10);
  const to = run.windowTo === null ? t('integrations.run.untilNow') : run.windowTo.slice(0, 10);
  return `${from} – ${to}`;
}

function SettingsTab({
  t,
  instance,
  onEdit,
  onDelete,
}: {
  readonly t: Translate;
  readonly instance: IntegrationInstance;
  readonly onEdit: (step: WizardStep) => void;
  readonly onDelete: () => void;
}) {
  const maps = mapSummary(t, instance);
  const on = activeRules(instance.rules);

  const cards: readonly {
    step: WizardStep;
    icon: 'link' | 'list-checks' | 'circle-dot' | 'flag' | 'user-round' | 'sliders-horizontal';
    title: string;
    lines: readonly string[];
  }[] = [
    {
      step: 'connection',
      icon: 'link',
      title: t('integrations.step.connection'),
      lines: [
        instance.baseUrl,
        instance.authKind === 'oauth'
          ? t('integrations.conn.oauth')
          : `${t('integrations.conn.tokenKind')} · ${instance.authEmail ?? instance.authHint ?? '–'}`,
      ],
    },
    {
      step: 'tasks',
      icon: 'list-checks',
      title: t('integrations.step.tasks'),
      lines: [
        instance.query.trim() === '' ? t('integrations.review.queryEmpty') : instance.query,
        `${t('integrations.field.since')}: ${instance.sinceOn}`,
      ],
    },
    {
      step: 'statuses',
      icon: 'circle-dot',
      title: t('integrations.step.statuses'),
      lines: [
        maps[0]?.value ?? '',
        instance.statuses
          .filter((entry) => entry.taskStatusId === null)
          .map((entry) => entry.remoteStatus)
          .join(', ') || t('integrations.map.none'),
      ],
    },
    /* J-5 · the priorities card, beside the statuses it mirrors. */
    {
      step: 'priorities',
      icon: 'flag',
      title: t('integrations.step.priorities'),
      lines: [
        maps[1]?.value ?? '',
        instance.priorities
          .filter((entry) => entry.taskPriorityId === null)
          .map((entry) => entry.remotePriority)
          .join(', ') || t('integrations.map.none'),
      ],
    },
    /**
     * C34 · the `fields` step (P2-20).
     *
     * ⚠️ **Its summary lines are deliberately not a count of what COULD be mapped.** Reading the
     * remote field list costs a round trip to the customer's Jira, and a settings card is drawn on
     * every visit — so this says what somebody chose, and the step itself does the asking.
     */
    {
      step: 'fields',
      icon: 'sliders-horizontal',
      title: t('integrations.step.fields'),
      lines: [t('integrations.fields.summary')],
    },
    /**
     *      * ┌─────────────────────────────────────────────────────────────────────┐
     *      * │ People — the wizard step that had no card here.
     *      * └─────────────────────────────────────────────────────────────────────┘
     *
     * ⚠️⚠️ **EVERY OTHER STEP OF THE WIZARD IS REACHABLE FROM THIS TAB AND THIS ONE WAS NOT.**
     * Reported as *"in the instance settings there's no box to connect people like there is for
     * the others"*, and that is exactly what it was: `WIZARD_STEPS` gained `people` with item 67
     * and the settings cards were written from the older list, so the only way back to the pairing
     * step was to re-walk the whole wizard.
     *
     * ⚠️ **It counts, it does not list.** `instance.users` is already on the screen — the
     * pairing rows themselves belong to the step, and a settings card's job is to say whether
     * there is anything to go and do.
     */
    {
      step: 'people',
      icon: 'user-round',
      title: t('integrations.step.people'),
      lines: [
        instance.users.length === 0
          ? t('integrations.people.none')
          : t('integrations.people.paired', {
              paired: instance.users.filter((user) => user.memberId !== null).length,
              count: instance.users.length,
            }),
        t('integrations.people.unpaired', {
          count: instance.users.filter((user) => user.memberId === null).length,
        }),
      ],
    },
    {
      step: 'rules',
      icon: 'sliders-horizontal',
      title: t('integrations.step.rules'),
      lines: [
        on.length === 0
          ? t('integrations.review.rulesEmpty')
          : on.map((rule) => ruleLabel(t, rule)).join(', '),
        `${t('integrations.overwatchAt', { at: instance.overwatchAt })} · ${t(
          'integrations.conflict',
          {
            winner: t(
              instance.conflictWinner === 'remote'
                ? 'integrations.conflict.remote'
                : 'integrations.conflict.local',
            ),
          },
        )}`,
      ],
    },
  ];

  return (
    <div className="flex flex-col gap-2.5">
      {cards.map((card) => (
        <div
          key={card.step}
          className="bg-card text-card-foreground flex items-start gap-3 rounded-xl border p-3.5 shadow-sm"
        >
          <span className="bg-muted text-muted-foreground mt-px grid size-8 shrink-0 place-items-center rounded-lg">
            <Icon name={card.icon} size={15} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-[13px] font-medium">{card.title}</div>
            <div className="mt-0.5 flex flex-col gap-0.5">
              {card.lines.map((line, index) => (
                <div key={index} className="text-muted-foreground truncate text-[12px]">
                  {line}
                </div>
              ))}
            </div>
          </div>
          {/* ⚠️ **One step, not the whole wizard.** Six steps to change a filter is why the mockup
              has `igWizOpen(..., single)` at all, and it is the difference between an editable
              instance and one somebody re-walks every time. */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              onEdit(card.step);
            }}
          >
            {t('common.edit')}
          </Button>
        </div>
      ))}

      <div className="border-destructive/40 bg-destructive/5 mt-1 rounded-xl border p-4">
        <h3 className="text-destructive text-[13px] font-medium">
          {t('integrations.dangerTitle')}
        </h3>
        <p className="text-muted-foreground mt-0.5 text-[12px] leading-relaxed">
          {t('integrations.deleteBody')}
        </p>
        <Button variant="destructive" size="sm" className="mt-3" onClick={onDelete}>
          {t('integrations.delete')}
        </Button>
      </div>
    </div>
  );
}
